export interface SkillItem {
  name: string;
  category: string;
}

export interface ProjectItem {
  title: string;
  description: string;
  tags: string[];
}

export interface Section {
  id: string;
  title: string;
  icon: string;
  code: string;
  color: string;
  position: [number, number, number];
  content: SectionContent;
}

export interface SectionContent {
  heading: string;
  description?: string;
  skills?: SkillItem[];
  projects?: ProjectItem[];
  experience?: ExperienceItem[];
  education?: EducationItem[];
  certifications?: string[];
  contact?: ContactInfo;
}

export interface ExperienceItem {
  role: string;
  company: string;
  period: string;
  subtitle: string;
  description: string;
  bullets?: string[];
}

export interface EducationItem {
  degree: string;
  institution: string;
  year: string;
}

export interface ContactInfo {
  email: string;
  github: string;
  linkedin: string;
}

const PHI = (1 + Math.sqrt(5)) / 2;

function fibonacciSphere(index: number, total: number, radius: number): [number, number, number] {
  const y = 1 - (index / (total - 1)) * 2;
  const radiusAtY = Math.sqrt(1 - y * y);
  const theta = 2 * Math.PI * index / PHI;
  return [
    Math.cos(theta) * radiusAtY * radius,
    y * radius,
    Math.sin(theta) * radiusAtY * radius,
  ];
}

export const sections: Section[] = [
  {
    id: 'about',
    title: 'About Me',
    icon: '👤',
    code: 'AB',
    color: '#00f0ff',
    position: fibonacciSphere(0, 11, 2.2),
    content: {
      heading: 'About Me',
      description:
        'DevOps & Platform Engineer building end-to-end delivery platforms on Kubernetes — containerisation, TLS ingress, CI/CD, pull-based GitOps with ArgoCD, and runtime security policy. I take an application as given and build the platform around it, tracing every architectural decision back to a real constraint.\n\nMy strongest work is hands-on: deploying workloads to Kind and k3s clusters, terminating TLS at nginx ingress, shipping images through GitHub Actions to GHCR, reconciling clusters against Git with ArgoCD, and hardening workloads with zero-trust KubeArmor/AppArmor policies. I currently work across Azure (AZ-104 & AZ-400 prep), Terraform, Prometheus and Grafana.\n\nThis builds on 3+ years of prior enterprise software development and production support — SAP and Linux environments, Python/Bash automation, and incident troubleshooting — which grounds my platform work in the realities of running production-critical systems.',
    },
  },
  {
    id: 'skills',
    title: 'Skills',
    icon: '⚡',
    code: 'SK',
    color: '#4060ff',
    position: fibonacciSphere(1, 11, 2.2),
    content: {
      heading: 'Core Technical Skills',
      skills: [
        { name: 'Kubernetes', category: 'Containers & Orchestration' },
        { name: 'Docker', category: 'Containers & Orchestration' },
        { name: 'Docker Compose', category: 'Containers & Orchestration' },
        { name: 'Kind', category: 'Containers & Orchestration' },
        { name: 'k3s', category: 'Containers & Orchestration' },
        { name: 'Helm', category: 'Containers & Orchestration' },
        { name: 'ArgoCD (GitOps)', category: 'CI/CD & GitOps' },
        { name: 'GitHub Actions', category: 'CI/CD & GitOps' },
        { name: 'GitLab CI/CD', category: 'CI/CD & GitOps' },
        { name: 'Azure DevOps (AZ-400)', category: 'CI/CD & GitOps' },
        { name: 'Jenkins', category: 'CI/CD & GitOps' },
        { name: 'GHCR', category: 'CI/CD & GitOps' },
        { name: 'Nexus Artifact Mgmt', category: 'CI/CD & GitOps' },
        { name: 'Microsoft Azure (AZ-104)', category: 'Cloud & IaC' },
        { name: 'Terraform', category: 'Cloud & IaC' },
        { name: 'AWS (EC2, VPC, IAM, S3)', category: 'Cloud & IaC' },
        { name: 'Ansible Awareness', category: 'Cloud & IaC' },
        { name: 'Prometheus', category: 'Monitoring & Observability' },
        { name: 'Grafana', category: 'Monitoring & Observability' },
        { name: 'Structured Logging', category: 'Monitoring & Observability' },
        { name: 'Health Probes & Runbooks', category: 'Monitoring & Observability' },
        { name: 'KubeArmor / AppArmor', category: 'Security' },
        { name: 'Zero-Trust Policy', category: 'Security' },
        { name: 'TLS / Ingress Termination', category: 'Security' },
        { name: 'IAM & Access Control', category: 'Security' },
        { name: 'Firewall Policy', category: 'Security' },
        { name: 'Python', category: 'Scripting & Development' },
        { name: 'Bash', category: 'Scripting & Development' },
        { name: 'Flask', category: 'Scripting & Development' },
        { name: 'pytest', category: 'Scripting & Development' },
        { name: 'REST APIs', category: 'Scripting & Development' },
        { name: 'scikit-learn', category: 'Scripting & Development' },
        { name: 'PostgreSQL', category: 'Databases' },
        { name: 'SQLAlchemy ORM', category: 'Databases' },
        { name: 'MySQL', category: 'Databases' },
        { name: 'SQLite', category: 'Databases' },
        { name: 'Ubuntu / Debian', category: 'Linux & Systems' },
        { name: 'nginx (Gateway/Proxy)', category: 'Linux & Systems' },
        { name: 'SSH & Networking', category: 'Linux & Systems' },
        { name: 'systemctl & Services', category: 'Linux & Systems' },
        { name: 'Jira & Agile/SDLC', category: 'Collaboration' },
        { name: 'Documentation', category: 'Collaboration' },
        { name: 'Remote Team Delivery', category: 'Collaboration' },
      ],
    },
  },
  {
    id: 'devops-cloud',
    title: 'DevOps & Cloud',
    icon: '☁️',
    code: 'DC',
    color: '#8040ff',
    position: fibonacciSphere(2, 11, 2.2),
    content: {
      heading: 'DevOps & Cloud Projects',
      projects: [
        {
          title: 'Wisecow — End-to-End Kubernetes Platform',
          description:
            'Took an open-source app (a shell script serving ASCII art over a netcat-based HTTP server) and built a full deployment platform around it. Containerised it on Ubuntu 24.04, deployed to Kubernetes (Kind and k3s) with TLS terminating at an nginx ingress, built a CI pipeline pushing to GHCR and verifying on an ephemeral cluster, added ArgoCD for pull-based GitOps with self-heal, and hardened the workload with a zero-trust KubeArmor policy enforced through AppArmor. Every decision traced back to a real constraint — from tcpSocket probes over httpGet, to converging the security whitelist under live enforcement.',
          tags: ['Kubernetes', 'Docker', 'ArgoCD', 'GitHub Actions', 'TLS', 'KubeArmor'],
        },
        {
          title: 'Kubernetes & Cloud HomeLab',
          description:
            'Hands-on lab environment for practising platform engineering and GitOps skills end-to-end. Deployed and managed Kubernetes workloads using YAML manifests — Deployments, Services, ConfigMaps, Namespaces — with health checks and availability configurations. Applied Terraform to provision cloud infrastructure with reusable modules, and implemented GitOps workflow patterns with structured branching and environment promotion strategies.',
          tags: ['Kubernetes', 'Terraform', 'GitOps', 'Linux'],
        },
      ],
    },
  },
  {
    id: 'kubernetes',
    title: 'K8s & Monitoring',
    icon: '🎯',
    code: 'KM',
    color: '#ff40c0',
    position: fibonacciSphere(3, 11, 2.2),
    content: {
      heading: 'Kubernetes & Monitoring',
      projects: [
        {
          title: 'Prometheus & Grafana Monitoring',
          description:
            'Configured Prometheus scraping and Grafana dashboards for containerised services — alerting rules, health check endpoints, and structured observability for multi-container deployments. Implemented metrics collection, built dashboard authoring, defined alert rules, and wrote runbook-style documentation for operational reliability.',
          tags: ['Prometheus', 'Grafana', 'Alerting', 'Runbooks', 'Observability'],
        },
        {
          title: 'Kubernetes Cluster Management',
          description:
            'Deploying and managing containerised workloads on Kubernetes — writing YAML manifests for Deployments, Services, ConfigMaps and Namespaces. Managing pod lifecycle, health checks and availability in mini-cluster environments.',
          tags: ['Kubernetes', 'YAML', 'Health Checks', 'Namespaces'],
        },
      ],
    },
  },
  {
    id: 'cicd',
    title: 'CI/CD',
    icon: '🔄',
    code: 'CI',
    color: '#00ff80',
    position: fibonacciSphere(4, 11, 2.2),
    content: {
      heading: 'CI/CD & DevOps Automation',
      projects: [
        {
          title: 'CI/CD and DevOps Automation Labs',
          description:
            'Implemented end-to-end CI/CD pipelines using GitHub Actions and GitLab CI/CD — integrating automated build, test, and deployment stages with YAML configuration and GitOps-aligned release workflows. Built containerised multi-service environments with Docker Compose. Managed artifact repository workflows with Nexus — pushing/pulling versioned images and maintaining release management practices.',
          tags: ['GitHub Actions', 'GitLab CI/CD', 'Docker Compose', 'Nexus', 'GitOps'],
        },
        {
          title: 'Jenkins Pipeline Automation',
          description:
            'Building and iterating CI/CD pipelines with Jenkins — automating build, test, and deployment stages with YAML configuration and Python-based validation scripts. Applying GitOps patterns for infrastructure change management.',
          tags: ['Jenkins', 'Python', 'GitOps', 'Docker'],
        },
      ],
    },
  },
  {
    id: 'fullstack',
    title: 'Full-Stack',
    icon: '🚀',
    code: 'FS',
    color: '#ffaa00',
    position: fibonacciSphere(5, 11, 2.2),
    content: {
      heading: 'Full-Stack & Automation Projects',
      projects: [
        {
          title: 'Kai Konane — STEM Learning Platform',
          description:
            'STEM learning platform for preschoolers with separate experiences for children, parents and teachers, and a scikit-learn model that predicts a starting level at registration. Built as a Flask monolith, then decomposed into four services (nginx gateway, web, JSON API, PostgreSQL) with a strict boundary — the web tier holds no ORM and reaches the domain only over HTTP. Runs the whole stack via Docker Compose with health checks and a single public port. Backed by 268 pytest tests (~80% API coverage) that assert on the API contract — payload shape and invariants, not just status codes.',
          tags: ['Flask', 'PostgreSQL', 'Docker Compose', 'nginx', 'pytest', 'scikit-learn'],
        },
        {
          title: 'XML-Driven Document Automation Pipeline',
          description:
            'Production ETL automation solution built for and deployed at Baraka IT Solutions. Built a modular Python pipeline that reads structured XML input to drive file routing, folder creation, and document upload workflows — with clear separation of responsibilities and full operational documentation. Resolved edge cases in XML parsing, permissions, and file handling.',
          tags: ['Python', 'Bash', 'Linux', 'ETL', 'XML'],
        },
      ],
    },
  },
  {
    id: 'sap',
    title: 'SAP & Enterprise',
    icon: '🏢',
    code: 'SE',
    color: '#ff6040',
    position: fibonacciSphere(6, 11, 2.2),
    content: {
      heading: 'SAP & Enterprise Work',
      projects: [
        {
          title: 'SAP Production Support & Automation',
          description:
            'Supported production-critical systems through structured incident triage — investigating issues across application logic, file handling, integrations and OS layers. Documented findings in knowledge-base entries for team use. Developed Python and Bash automation scripts for file upload pipelines, XML-driven routing, and folder structure management — reducing manual operational work.',
          tags: ['SAP', 'Python', 'Bash', 'Production Support', 'Incident Triage'],
        },
        {
          title: 'Linux Environment Administration',
          description:
            'Configured and maintained Linux environments across development, test and production — applying change control processes and resolving daemon errors, permission issues, package conflicts, and repository misconfigurations.',
          tags: ['Linux', 'Change Control', 'Troubleshooting', 'Dev/Test/Prod'],
        },
      ],
    },
  },
  {
    id: 'experience',
    title: 'Experience',
    icon: '💼',
    code: 'XP',
    color: '#40c0ff',
    position: fibonacciSphere(7, 11, 2.2),
    content: {
      heading: 'Work Experience',
      experience: [
        {
          role: 'DevOps Engineer — Internship',
          company: 'KubeCraft',
          period: 'Jun 2025 – Present',
          subtitle: 'Kubernetes, CI/CD, Infrastructure as Code, Observability',
          description:
            'Practical DevOps internship focused on Kubernetes, CI/CD, Infrastructure as Code, observability and cloud infrastructure — all hands-on in lab and cloud environments.',
          bullets: [
            'Deploying and managing containerised workloads on Kubernetes — YAML manifests, pod lifecycle, health checks in Rancher Desktop',
            'Building CI/CD pipelines with GitHub Actions and Jenkins — GitOps patterns for infrastructure change management',
            'Provisioning cloud infrastructure using Terraform — reusable modules for VPCs, compute and networking',
            'Implementing Prometheus-based monitoring — Grafana dashboards, alert rules, runbook documentation',
            'Administering Linux lab environments — SSH, firewall rules, log analysis, service troubleshooting',
          ],
        },
        {
          role: 'SAP / Software Developer',
          company: 'Baraka IT Solutions',
          period: 'Mar 2023 – Jun 2026',
          subtitle: 'Automation Engineering, Back-end Development, Production Support',
          description:
            'Full-stack development and production support in an enterprise SAP and Linux environment — automation engineering, back-end development, change management, and cross-functional delivery.',
          bullets: [
            'Developed Python and Bash automation scripts for file upload pipelines, XML-driven routing, and folder structure management',
            'Built and supported a production XML-driven ETL workflow with modular design and full documentation',
            'Configured and maintained Linux environments across dev, test and production with change control processes',
            'Supported production-critical systems through structured incident triage across application logic, file handling, integrations and OS layers',
            'Collaborated with developers, technical leads, and business stakeholders to integrate automation into existing workflows',
          ],
        },
      ],
    },
  },
  {
    id: 'education',
    title: 'Education',
    icon: '🎓',
    code: 'ED',
    color: '#a080ff',
    position: fibonacciSphere(8, 11, 2.2),
    content: {
      heading: 'Education & Certifications',
      education: [
        {
          degree: 'Advanced Diploma in Computer Sciences — NQF Level 7',
          institution: 'Tshwane University of Technology',
          year: '2024',
        },
        {
          degree: 'National Diploma in IT: Software Development — NQF Level 6',
          institution: 'Tshwane University of Technology',
          year: '2022',
        },
      ],
      certifications: [
        'In progress — Microsoft Azure Administrator (AZ-104) & Azure DevOps Engineer Expert (AZ-400), hands-on exam preparation',
        'IBM Applied DevOps Engineering Certificate — GitLab CI/CD, Docker, Kubernetes, automated testing, deployment pipelines',
        'IBM DevOps and Software Engineering Certificate — Agile, CI/CD, containerisation, cloud-native concepts, deployment workflows',
      ],
    },
  },
  {
    id: 'contact',
    title: 'Contact',
    icon: '📬',
    code: 'CT',
    color: '#00f0ff',
    position: fibonacciSphere(9, 11, 2.2),
    content: {
      heading: 'Get In Touch',
      description: "I'm always open to new opportunities, collaborations, and conversations about DevOps, Cloud, and Platform Engineering. Let's connect!",
      contact: {
        email: 'lsktheledi@gmail.com',
        github: 'https://github.com/naughlan-creator/',
        linkedin: 'https://www.linkedin.com/in/lehlogonolo-theledi-66a7b2285/',
      },
    },
  },
];

export const categoryColors: Record<string, string> = {
  'Containers & Orchestration': '#8040ff',
  'CI/CD & GitOps': '#00ff80',
  'Cloud & IaC': '#ff40c0',
  'Monitoring & Observability': '#ffaa00',
  'Security': '#ff4060',
  'Scripting & Development': '#ff6040',
  'Databases': '#4060ff',
  'Linux & Systems': '#00f0ff',
  'Collaboration': '#a080ff',
};
